import { Controller } from "stimulus"

// Connects to data-controller="transaction-chat"
export default class extends Controller {
  static targets = ["messageInput", "submitButton", "chatDrawer", "drawerTab", "mainForm", "minimizeButton"]
  
  get sessionId() {
    return this.data.get("sessionId")
  }

  connect() {
    this.scrollChatToBottom()
    this.isMinimized = false
    // Start with drawer expanded and form properly positioned
    this.adjustMainFormPadding()
  }

  handleKeydown(event) {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault()
      this.element.querySelector('form').dispatchEvent(new Event('submit', {bubbles: true}))
    }
  }

  addMessageToChat(role, content, timestamp) {
    const chatHistory = document.getElementById("chat_history")
    
    // Create message element
    const messageDiv = document.createElement("div")
    messageDiv.className = `chat-message ${role === 'user' ? 'ml-8' : 'mr-8'}`
    
    const alignmentClass = role === 'user' ? 'justify-end' : 'justify-start'
    const colorClass = role === 'user' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-800'
    const roleLabel = role === 'user' ? 'You' : 'AI Assistant'
    
    messageDiv.innerHTML = `
      <div class="flex ${alignmentClass}">
        <div class="max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${colorClass}">
          <div class="text-sm font-medium mb-1">${roleLabel}</div>
          <div class="text-sm">${this.escapeHtml(content)}</div>
          <div class="text-xs opacity-75 mt-1">${this.formatTime(timestamp)}</div>
        </div>
      </div>
    `
    
    chatHistory.appendChild(messageDiv)
    this.scrollChatToBottom()
  }

  addLoadingMessage() {
    const chatHistory = document.getElementById("chat_history")
    
    const loadingDiv = document.createElement("div")
    loadingDiv.className = "chat-message mr-8"
    loadingDiv.id = "loading-message"
    
    loadingDiv.innerHTML = `
      <div class="flex justify-start">
        <div class="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-gray-100 text-gray-600 border border-gray-200">
          <div class="text-sm font-medium mb-1">AI Assistant</div>
          <div class="text-sm flex items-center">
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
            <span class="ml-2">Thinking...</span>
          </div>
        </div>
      </div>
    `
    
    chatHistory.appendChild(loadingDiv)
    this.scrollChatToBottom()
  }

  removeLoadingMessage() {
    const loadingMessage = document.getElementById("loading-message")
    if (loadingMessage) {
      loadingMessage.remove()
    }
  }

  scrollChatToBottom() {
    const chatHistory = document.getElementById("chat_history")
    if (chatHistory) {
      chatHistory.scrollTop = chatHistory.scrollHeight
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  formatTime(date) {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }

  // Drawer control methods
  minimizeDrawer() {
    console.log("minimizeDrawer called")
    if (this.hasChatDrawerTarget) {
      this.chatDrawerTarget.style.transform = "translateX(100%)"
      console.log("Drawer minimized")
    }
    if (this.hasDrawerTabTarget) {
      this.drawerTabTarget.classList.remove("hidden")
      console.log("Tab shown")
    }
    this.isMinimized = true
    this.adjustMainFormPadding()
  }

  expandDrawer() {
    if (this.hasChatDrawerTarget) {
      this.chatDrawerTarget.style.transform = "translateX(0%)"
    }
    if (this.hasDrawerTabTarget) {
      this.drawerTabTarget.classList.add("hidden")
    }
    this.isMinimized = false
    this.adjustMainFormPadding()
  }

  closeDrawer() {
    if (this.hasChatDrawerTarget) {
      this.chatDrawerTarget.style.transform = "translateX(100%)"
    }
    if (this.hasDrawerTabTarget) {
      this.drawerTabTarget.classList.add("hidden")
    }
    this.isMinimized = true
    this.adjustMainFormPadding()
  }

  // Adjust main form padding based on drawer state
  adjustMainFormPadding() {
    if (this.hasMainFormTarget) {
      if (this.isMinimized) {
        this.mainFormTarget.style.marginRight = "0"
      } else {
        this.mainFormTarget.style.marginRight = "24rem" // w-96 = 24rem
      }
    }
  }


  // When user focuses on chat, ensure drawer is expanded
  onChatFocus() {
    if (this.isMinimized) {
      this.expandDrawer()
    }
  }

  // Remove old ensureChatExpanded method and update submitMessage
  submitMessage(event) {
    if (!this.hasMessageInputTarget) return
    
    const message = this.messageInputTarget.value.trim()
    
    if (!message) {
      event.preventDefault()
      return
    }

    // Ensure drawer is expanded when sending messages
    if (this.isMinimized) {
      this.expandDrawer()
    }

    // Add user message to chat immediately for better UX
    this.addMessageToChat("user", message, new Date())
    
    // Add loading interstitial
    this.addLoadingMessage()
    
    // DON'T clear the input here - let the form submit first
    // this.messageInputTarget.value = ""
    
    // Disable submit button temporarily
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = "Sending..."
    }
  }

  // Called after turbo stream updates
  messageAdded() {
    this.scrollChatToBottom()
    
    // Re-enable submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Send"
    }
    
    // Clear the input after successful submission
    if (this.hasMessageInputTarget) {
      this.messageInputTarget.value = ""
      this.messageInputTarget.focus()
    }
  }
}